import React, { ReactNode, useCallback, useMemo } from 'react';
import Draggable from 'react-draggable';
import { Resizable, ResizeCallback } from 're-resizable';
import { Dialog, PaperProps } from '@mui/material';
import { DefaultTheme, makeStyles } from '@mui/styles';
import { Gripper } from 'spw-components/global/Gripper';
import GrabAreaGrayIcon from 'spw-static/images/grab-area-gray.svg';
import { colors } from 'spw-theme';
import { Z_INDEX_HIGHER_THAN_BREADCRUMBS } from 'spw-constants';
import { SPWSpinner } from './spinners';
import { DraggablePaper } from './draggable-paper';
import { Column } from '.';

const ESTIMATED_SCROLLBAR_WIDTH = 16;
const RIGHT_OFFSET = 17;

const useStyles = makeStyles<DefaultTheme, { minHeight: number }>({
  wrapper: {
    boxShadow:
      'rgba(0, 0, 0, 0.2) 0px 2px 1px -1px, rgba(0, 0, 0, 0.14) 0px 1px 1px 0px, rgba(0, 0, 0, 0.12) 0px 1px 3px 0px',
    borderRadius: '9px',
  },
  resizeGripperWrapper: {
    height: '100px',
    display: 'flex',
    alignItems: 'flex-end',
  },
  backdrop: {
    backgroundColor: 'unset',
  },
  gripper: {
    '&&': {
      backgroundColor: colors.containerBorder,
      '& > img': {
        width: '16px',
      },
      '&.drag': {
        cursor: 'move',
      },
      '&.resize': {
        cursor: 'ns-resize',
        width: '100%',
      },
    },
  },
  paper: {
    borderRadius: '9px',
    boxShadow: 'none',
    position: 'absolute',
    minHeight: ({ minHeight }) => minHeight,
    zIndex: Z_INDEX_HIGHER_THAN_BREADCRUMBS,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'unset',
    maxWidth: 'unset',
  },
  actions: {
    padding: '20px 24px',
    marginTop: '0',
    backgroundColor: 'white',
    borderRadius: '0 0 9px 9px',

    '& > button': {
      zIndex: 1,
      '&:first-child': {
        marginRight: 16,
      },
      '&&': {
        width: 88,
      },
    },
  },
  footer: {
    height: '76px',
    borderRadius: '0 0 9px 9px',
  },
});

export const useDefaultPosition = (
  anchorEl: Element,
  width: number,
  minHeight: number,
  isVerticallyCenteredByDefault: boolean
) =>
  useMemo(() => {
    const anchorRect = anchorEl?.getBoundingClientRect();

    let top = 0;
    let left = 0;

    if (anchorRect) {
      top = anchorRect.top;
      left = anchorRect.right + RIGHT_OFFSET;

      if (left + width > window.innerWidth - ESTIMATED_SCROLLBAR_WIDTH) {
        left = anchorRect.left - width;
      }

      if (anchorRect.top + minHeight > window.innerHeight) {
        top = window.innerHeight - minHeight;
      }
    }

    if (isVerticallyCenteredByDefault) {
      top = (window.innerHeight - minHeight) / 2;
    }

    return {
      top,
      left,
    };
  }, [anchorEl, width, minHeight, isVerticallyCenteredByDefault]);

type Props = {
  anchorEl: Element;
  onClose: () => void;
  onResize?: ResizeCallback;
  width: number;
  minHeight?: number;
  loading?: boolean;
  isVerticallyCenteredByDefault?: boolean;
  disableResize?: boolean;
  ContentWrapper: React.FC<{ children: ReactNode }>;
  title: ReactNode;
  children: ReactNode;
  initialPosition?: { top: number; left: number };
};

const DraggableResizableDialog = ({
  anchorEl,
  onClose,
  onResize,
  width,
  minHeight,
  isVerticallyCenteredByDefault = false,
  loading = false,
  disableResize = false,
  ContentWrapper,
  title,
  children,
  initialPosition,
}: Props) => {
  const { top, left } =
    initialPosition ??
    useDefaultPosition(
      anchorEl,
      width,
      minHeight,
      isVerticallyCenteredByDefault
    );
  const classes = useStyles({ minHeight });

  const gripperProps = {
    className: classes.gripper,
    GrabIcon: GrabAreaGrayIcon,
  };

  const PaperComponent = useCallback(
    ({ className, ...props }: PaperProps) => {
      return (
        <Draggable
          bounds="#react-container"
          handle="#draggable-bulk-edit-dialog"
        >
          <DraggablePaper
            {...props}
            className={`${classes.paper} ${className ?? ''}`}
          />
        </Draggable>
      );
    },
    [classes]
  );

  return (
    <Dialog
      open={!!anchorEl}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-bulk-edit-dialog"
      style={{
        width: 0,
        height: 0,
      }}
      hideBackdrop
      onClose={onClose}
      disableEnforceFocus
      disableScrollLock
      PaperProps={{
        style: {
          zIndex: Z_INDEX_HIGHER_THAN_BREADCRUMBS,
          top,
          left,
        },
      }}
      BackdropProps={{
        className: classes.backdrop,
      }}
    >
      <Resizable
        bounds="window"
        defaultSize={{ width, height: minHeight }}
        enable={{ bottom: !disableResize }}
        onResize={onResize}
        minHeight={minHeight}
        handleStyles={{
          bottom: {
            height: 'fit-content',
            transform: 'translateY(-4px)',
          },
        }}
        handleComponent={{
          bottom: !disableResize ? (
            <div className={classes.resizeGripperWrapper}>
              <Gripper
                isBottom
                GrabIcon={GrabAreaGrayIcon}
                className={`${gripperProps.className} resize`}
              />
            </div>
          ) : undefined,
        }}
      >
        <ContentWrapper>
          {!loading ? (
            <>
              <div id="draggable-bulk-edit-dialog" className="drag-handle">
                <Gripper
                  GrabIcon={GrabAreaGrayIcon}
                  className={`${gripperProps.className} drag`}
                />
                {title}
              </div>
              {children}
            </>
          ) : (
            <Column height="100%" justifyContent="center" alignItems="center">
              <SPWSpinner color={colors.mainBlue} size="25px" />
            </Column>
          )}
        </ContentWrapper>
      </Resizable>
    </Dialog>
  );
};

export default DraggableResizableDialog;
